import React, { useState } from 'react';
import {
	Box,
	Paper,
	Typography,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	CircularProgress,
	FormHelperText,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import InputMask from 'react-input-mask';
import BasicDatePicker from 'core/components/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import Autocomplete from '@mui/material/Autocomplete';
import {
	COUNTRY_LIST,
	COUNTRY_STATES,
} from '../../settings/settingsGameMasterTabs/GeneralSetting/constants';
import settingsUtils from '../../settings/settingsGameMasterTabs/GeneralSetting/utils';
import { Button } from 'components/ui/button';
import { OnboardingRecord, updateOnboardingBasicInfo } from 'core/API/onboarding';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import style from './EmployeeSelfEntryForm.module.scss';

interface EmployeeSelfEntryFormProps {
	onboardingRecord: OnboardingRecord;
	editable: boolean;
	onSubmit: () => void;
}

export function EmployeeSelfEntryForm({
	onboardingRecord,
	editable,
	onSubmit,
}: EmployeeSelfEntryFormProps): JSX.Element {
	const { showToast } = useToastContext();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const validationSchema = Yup.object().shape({
		firstName: Yup.string().required('First name is required'),
		lastName: Yup.string().required('Last name is required'),
		phone: Yup.string()
			.required('Phone number is required')
			.test('is-complete', 'Please enter a complete phone number', (value) => {
				if (!value) return false;
				return value.replace(/[^0-9]/g, '').length === 10;
			}),
		dateOfBirth: Yup.mixed()
			.required('Date of birth is required')
			.test('is-valid-date', 'Please enter a valid date of birth', (value) => {
				if (!value) return true; // let required() handle null
				return dayjs(value as any).isValid();
			})
			.test('min-age', 'You must be at least 10 years old', (value) => {
				if (!value) return true;
				return dayjs(value as any).isBefore(dayjs().subtract(10, 'year'));
			})
			.test('max-age', 'Invalid date of birth', (value) => {
				if (!value) return true;
				return dayjs(value as any).isAfter(dayjs().subtract(100, 'year'));
			}),
		maritalStatus: Yup.string().required('Marital status is required'),
		gender: Yup.string().required('Gender is required'),
		addressLine1: Yup.string().required('Address Line 1 is required'),
		city: Yup.string().required('City is required'),
		state: Yup.string().required('State is required'),
		postcode: Yup.string()
			.required('Postal code is required')
			.test('is-valid-postal-code', 'Invalid postal code', function (value) {
				const country = (this.parent as any)?.country;
				return !value ? false : settingsUtils.isValidPostalCode(value, country);
			}),
		country: Yup.string().required('Country is required'),
	});

	const formik = useFormik({
		initialValues: {
			firstName: onboardingRecord.firstName || '',
			middleName: onboardingRecord.middleName || '',
			lastName: onboardingRecord.lastName || '',
			phone: onboardingRecord.cellPhone || '',
			dateOfBirth: onboardingRecord.dateOfBirth ? dayjs(onboardingRecord.dateOfBirth) : null as Dayjs | null,
			maritalStatus: onboardingRecord.maritalStatus || 'Single',
			gender: onboardingRecord.gender || '',
			addressLine1: onboardingRecord.addressLine1 || '',
			addressLine2: onboardingRecord.addressLine2 || '',
			city: onboardingRecord.city || '',
			state: onboardingRecord.state || '',
			postcode: onboardingRecord.postcode || '',
			country: onboardingRecord.country || 'USA',
		},
		validationSchema,
		onSubmit: async (values) => {
			if (!editable) return;
			try {
				setIsSubmitting(true);

				// Prepare the payload for PATCH api/onboardings/{id}/basic_info
				const payload = {
					firstName: values.firstName,
					middleName: values.middleName,
					lastName: values.lastName,
					cellPhone: values.phone,
					gender: values.gender,
					maritalStatus: values.maritalStatus,
					addressLine1: values.addressLine1,
					addressLine2: values.addressLine2,
					city: values.city,
					postcode: values.postcode,
					state: values.state,
					country: values.country,
					dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : '',
				};

				await updateOnboardingBasicInfo(onboardingRecord.id, payload);
				showToast('success', 'Your information has been saved successfully!');
				onSubmit();
			} catch (error: any) {
				console.error('Error updating onboarding record:', error);
				showToast(
					'error',
					error?.response?.data?.message || 'Failed to save information. Please try again.'
				);
			} finally {
				setIsSubmitting(false);
			}
		},
	});

	const getStateOptions = () => {
		if (!formik.values.country) return [];
		return COUNTRY_STATES[formik.values.country] || [];
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		// Explicitly mark all fields as touched so validation messages appear
		formik.setTouched({
			firstName: true,
			lastName: true,
			phone: true,
			dateOfBirth: true,
			maritalStatus: true,
			gender: true,
			addressLine1: true,
			city: true,
			state: true,
			postcode: true,
			country: true,
		}, false);
		formik.handleSubmit(e as any);
	};

	return (
		<Paper className={style.FormPaper}>
			<form onSubmit={handleSubmit}>
				<Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
					Your Personal Information
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					{editable
						? 'Please fill in your personal details. Your manager has already entered some basic information which you can confirm or update.'
						: 'Your basic information has been submitted and is currently under review. It cannot be edited at this time.'}
				</Typography>

				{/* Basic Information Section */}
				<Typography variant="subtitle1" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
					Basic Information
				</Typography>

				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
					<TextField
						label="First Name"
						name="firstName"
						value={formik.values.firstName}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.firstName && Boolean(formik.errors.firstName)}
						helperText={formik.touched.firstName && formik.errors.firstName}
						fullWidth
						required
						disabled={!editable}
					/>
					<TextField
						label="Middle Name"
						name="middleName"
						value={formik.values.middleName}
						onChange={formik.handleChange}
						fullWidth
						disabled={!editable}
					/>
					<TextField
						label="Last Name"
						name="lastName"
						value={formik.values.lastName}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.lastName && Boolean(formik.errors.lastName)}
						helperText={formik.touched.lastName && formik.errors.lastName}
						fullWidth
						required
						disabled={!editable}
					/>
				</Box>

				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3, alignItems: 'start' }}>
					<Box sx={{ minWidth: 0 }}>
						<BasicDatePicker
							label="Date of Birth"
							name="dateOfBirth"
							value={formik.values.dateOfBirth}
							onChange={(date) => editable ? formik.setFieldValue('dateOfBirth', date) : undefined}
							required
							error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
							helperText={(formik.touched.dateOfBirth && (formik.errors.dateOfBirth as string)) || ''}
							margin="none"
						/>
					</Box>

					<Box sx={{ minWidth: 0 }}>
						<InputMask
							mask="(999) 999 - 9999"
							value={formik.values.phone}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							maskChar="_"
							disabled={!editable}
						>
							{(inputProps: any) => (
								<TextField
									{...inputProps}
									label="Cell Phone"
									name="phone"
									fullWidth
									required
									error={formik.touched.phone && Boolean(formik.errors.phone)}
									helperText={formik.touched.phone && formik.errors.phone}
								/>
							)}
						</InputMask>
					</Box>
				</Box>

				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
					<FormControl
						fullWidth
						required
						error={formik.touched.maritalStatus && Boolean(formik.errors.maritalStatus)}
					>
						<InputLabel id="marital-status-label">Marital Status</InputLabel>
						<Select
							labelId="marital-status-label"
							name="maritalStatus"
							value={formik.values.maritalStatus}
							label="Marital Status"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={!editable}
						>
							<MenuItem value="Single">Single</MenuItem>
							<MenuItem value="Married">Married</MenuItem>
							<MenuItem value="Divorced">Divorced</MenuItem>
							<MenuItem value="Widowed">Widowed</MenuItem>
						</Select>
						{formik.touched.maritalStatus && formik.errors.maritalStatus && (
							<FormHelperText>{formik.errors.maritalStatus}</FormHelperText>
						)}
					</FormControl>

					<FormControl
						fullWidth
						required
						error={formik.touched.gender && Boolean(formik.errors.gender)}
					>
						<InputLabel id="gender-label">Gender</InputLabel>
						<Select
							labelId="gender-label"
							name="gender"
							value={formik.values.gender}
							label="Gender"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={!editable}
						>
							<MenuItem value="Male">Male</MenuItem>
							<MenuItem value="Female">Female</MenuItem>
							<MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
						</Select>
						{formik.touched.gender && formik.errors.gender && (
							<FormHelperText>{formik.errors.gender}</FormHelperText>
						)}
					</FormControl>
				</Box>

				{/* Address Section */}
				<Typography variant="subtitle1" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
					Address
				</Typography>

				<TextField
					label="Address Line 1"
					name="addressLine1"
					value={formik.values.addressLine1}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
					helperText={formik.touched.addressLine1 && (formik.errors.addressLine1 as string)}
					fullWidth
					required
					disabled={!editable}
					sx={{ mb: 2 }}
				/>

				<TextField
					label="Address Line 2"
					name="addressLine2"
					value={formik.values.addressLine2}
					onChange={formik.handleChange}
					fullWidth
					disabled={!editable}
					sx={{ mb: 2 }}
				/>

				<TextField
					label="City"
					name="city"
					value={formik.values.city}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.city && Boolean(formik.errors.city)}
					helperText={formik.touched.city && formik.errors.city}
					fullWidth
					required
					disabled={!editable}
					sx={{ mb: 2 }}
				/>

				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
					<FormControl fullWidth margin="normal">
						<Autocomplete
							options={getStateOptions()}
							getOptionLabel={(option) => option}
							value={formik.values.state || null}
							onChange={(_, newValue) => {
								formik.setFieldValue('state', newValue || '');
							}}
							disabled={!editable || !formik.values.country}
							renderInput={(params) => (
								<TextField
									{...params}
									label="State"
									error={(formik.touched.state || formik.submitCount > 0) && Boolean(formik.errors.state)}
									helperText={(formik.touched.state || formik.submitCount > 0) && (formik.errors.state as string)}
									required
								/>
							)}
						/>
					</FormControl>

					<TextField
						label="Postal Code"
						name="postcode"
						value={formik.values.postcode}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.postcode && Boolean(formik.errors.postcode)}
						helperText={formik.touched.postcode && formik.errors.postcode}
						fullWidth
						required
						disabled={!editable}
						sx={{ mt: 2 }}
					/>
				</Box>

				<FormControl fullWidth margin="normal" sx={{ mb: 3 }}>
					<Autocomplete
						options={COUNTRY_LIST}
						getOptionLabel={(option) => option}
						value={formik.values.country || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('country', newValue || '');
							formik.setFieldValue('state', ''); // Reset state when country changes
						}}
						disabled={!editable}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Country"
								error={(formik.touched.country || formik.submitCount > 0) && Boolean(formik.errors.country)}
								helperText={(formik.touched.country || formik.submitCount > 0) && (formik.errors.country as string)}
								required
							/>
						)}
					/>
				</FormControl>

				{/* Action Buttons */}
				{editable && (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
						<Button
							type="submit"
							disabled={isSubmitting}
							className={style.SubmitButton}
							style={{ padding: '5px', borderRadius: '5px' }}
						>
							{isSubmitting ? <CircularProgress size={24} /> : 'Save & Continue'}
						</Button>
					</Box>
				)}
			</form>
		</Paper>
	);
}
